/**
 * Utility function to ensure our max chunk size is safe with the file
 * upload API.  If a chunk size not divisible by 320kB is sent, the service
 * assumes it is the last chunk. So always return a size divisible by 320kB
 * @param requestedChunkSize The desired max chunk size
 * @param minMaxSafeChunkSize The minimum largest safe size for a chunk unit in bytes
 */ export function getSafeChunkSize(requestedChunkSize, minMaxSafeChunkSize) {
    if (requestedChunkSize % minMaxSafeChunkSize === 0) {
        return requestedChunkSize;
    }
    return Math.ceil(requestedChunkSize / minMaxSafeChunkSize) * minMaxSafeChunkSize;
}
/**
 * Convert given base 64 string into an array buffer
 * @param base64string Base 64 string to convert
 */ export function convertBase64ToBytes(base64string) {
    // Convert to binary data
    const bytesDataString = window.atob(base64string);
    const bytesData = new Uint8Array(bytesDataString.length);
    for(let i = bytesDataString.length - 1; i >= 0; i--){
        bytesData[i] = bytesDataString.charCodeAt(i);
    }
    return bytesData.buffer;
}
/**
 * Get the content range header value for given params
 * @param initialByteIndex The initial byte index of the range
 * @param finalByteIndex The final byte index of the range
 * @param totalSize The total size
 */ export function getContentRangeHeaderValue(initialByteIndex, finalByteIndex, totalSize) {
    if (initialByteIndex < 0 || finalByteIndex < 0 || initialByteIndex > finalByteIndex || finalByteIndex >= totalSize) {
        throw new Error("invalid content-range");
    }
    return `bytes ${initialByteIndex}-${finalByteIndex}/${totalSize}`;
}
export function generateValidFilename(proposedFilename) {
    const invalidCharactersRegex = /[/\\?%*:|"<>#%]/g;
    return proposedFilename.replace(invalidCharactersRegex, "-");
}
/**
 * Calculate the chunk parameters for uploading part of a file
 * @param fileSize The total size of the file
 * @param bytesSent The number of bytes already sent
 * @param maxChunkBytes The maximum chunk size in bytes
 * @returns The chunk parameters with initial and final byte indices
 */ export function calculateFileChunkParams(fileSize, bytesSent, maxChunkBytes) {
    if (maxChunkBytes <= 0) {
        throw new Error("maxChunkBytes must be greater than 0");
    }
    const initialByteIndex = bytesSent || 0;
    let chunkSize = fileSize - bytesSent;
    if (chunkSize > maxChunkBytes) {
        chunkSize = maxChunkBytes;
    }
    const finalByteIndex = initialByteIndex + chunkSize - 1;
    return {
        initialByteIndex,
        finalByteIndex
    };
}
/**
 * Read a chunk of bytes from a file as a base64 string
 * @param file The file to read from
 * @param fileReader The FileReader instance to use
 * @param initialByteIndex The starting byte index
 * @param finalByteIndex The ending byte index (inclusive)
 * @returns Promise that resolves to the base64-encoded data URL
 */ export async function readFileBytesAsBase64Async(file, fileReader, initialByteIndex, finalByteIndex) {
    return new Promise((resolve, reject)=>{
        fileReader.onerror = (evt)=>{
            reject(evt);
        };
        fileReader.onload = async (evt)=>resolve(evt.target.result);
        const blob = file.slice(initialByteIndex, finalByteIndex + 1);
        fileReader.readAsDataURL(blob);
    });
}
/**
 * Extract the base64 data from a data URL string
 * @param dataUrl The data URL string (e.g., "data:text/plain;base64,SGVsbG8=")
 * @returns The base64 string without the data URL prefix
 */ export function extractBase64FromDataUrl(dataUrl) {
    const indexOfBase64 = dataUrl.indexOf("base64,");
    if (indexOfBase64 > -1) {
        return dataUrl.substring(indexOfBase64 + 7);
    }
    return dataUrl;
}
